import { Container, Graphics, Text } from 'pixi.js';

/**
 * "+1 PEG · BOOST" telegraph callout — sits next to the local
 * player's seat during STAKE when they've armed a BOOST /
 * TRIPLE_BOOST. Producer brief 2026-05-16: the badge must stay
 * BENEATH the avatar's z-index so it visually tucks behind the
 * portrait when the two overlap, and outward from the wheel
 * centre so it never paints over the gold rim.
 */

const FONT_FAMILY = '"Fredoka", system-ui, -apple-system, sans-serif';

export interface WheelResolveBoostTelegraphOptions {
  /** Avatar diameter used to size the badge and to compute the
   *  radial offset from the seat avatar centre. */
  avatarDiameter: number;
}

export class WheelResolveBoostTelegraph extends Container {
  private readonly bg: Graphics;
  private readonly labelText: Text;
  private readonly opts: WheelResolveBoostTelegraphOptions;

  constructor(options: WheelResolveBoostTelegraphOptions) {
    super();
    this.opts = options;
    this.visible = false;

    this.bg = new Graphics();
    this.addChild(this.bg);

    this.labelText = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 10,
        fontWeight: '900',
        fill: 0x3a2700,
        letterSpacing: 0.4,
      },
    });
    this.labelText.anchor.set(0.5);
    this.addChild(this.labelText);
  }

  /** Refresh visibility + pip count. Pass {@code 0} pegs to hide. */
  setPegs(boostCount: number, tripleBoostCount: number): void {
    const totalPegs = boostCount + tripleBoostCount * 3;
    if (totalPegs <= 0) {
      this.visible = false;
      return;
    }
    const text =
      tripleBoostCount > 0
        ? `+${totalPegs} PEG · 3× BOOST`
        : totalPegs > 1
          ? `+${totalPegs} PEG · BOOST`
          : '+1 PEG · BOOST';
    this.labelText.text = text;
    // Update the bg shape now that we know the label width.
    const padX = 6;
    const padY = 2;
    const w = Math.ceil(this.labelText.width) + padX * 2;
    const h = Math.ceil(this.labelText.height) + padY * 2;
    this.bg.clear();
    this.bg
      .roundRect(-w / 2, -h / 2, w, h, 8)
      .fill({ color: 0xffc940, alpha: 1 })
      .roundRect(-w / 2, -h / 2, w, h, 8)
      .stroke({ color: 0xffffff, alpha: 1, width: 1.5 });
    this.visible = true;
  }

  /** Position the callout radially outward from the seat avatar
   *  centre. {@code pointerAngleDeg} = 0 → straight up; 90 →
   *  right; 180 → down. The host scene passes the seat's
   *  perimeter angle here.
   *
   *  Producer feedback 2026-05-17 — "the label appeared behind the
   *  wheel". The previous dist (0.95 × avatar) put the label centre
   *  just past the avatar's outer edge but the label is a wide
   *  horizontal pill, so its INWARD edge crossed back over the rim
   *  on some slot angles. Bump to 1.5× so the label's pill sits
   *  clearly past the rim band for every seat. */
  setRadialOffset(pointerAngleDeg: number): void {
    const radialAngleRad = ((pointerAngleDeg - 90) * Math.PI) / 180;
    const dist = this.opts.avatarDiameter * 1.5;
    this.x = Math.cos(radialAngleRad) * dist;
    this.y = Math.sin(radialAngleRad) * dist;
  }

  destroyDisplay(): void {
    this.removeChildren();
  }
}
